/**
 * Attribution, as the deck shapes it (DESIGN.md §3.6): a chip at the end of the
 * two rows anyone could score on. `Leo ✓ · Nina ✓ · You ✓` on rank 1,
 * `Tom ✗ · Isha ✓` on rank last.
 *
 * It used to be a pair of sentences per row — *"4 people had you for bottom —
 * Sam, Priya, Alex, Jo."* — arriving as its own beat. The receipts still matter
 * (SPEC §5.2: the predictors are on the hook too, which is what keeps the room
 * talking), but a paragraph under every row buries the ranking it is annotating,
 * and the deck's reveal is a ranking first.
 *
 * A chip lists everyone who named that player at *either* end, not just the end
 * they landed on. Restricting it to the correct callers would make every mark a
 * ✓ and the mark would then say nothing; the interesting name on rank 12 is the
 * person who had them for first.
 */
import type { Attribution, PlayerId } from '@idgame/protocol';

export type RevealEnd = 'first' | 'last';

export interface AttributionMark {
  playerId: PlayerId;
  /** "Leo", or "You" for the person reading. */
  name: string;
  /** True when this predictor called the end the player actually landed on. */
  correct: boolean;
}

export interface AttributionChipInput {
  /** Which end of the ranking this row is. */
  end: RevealEnd;
  attribution: Attribution | undefined;
  names: ReadonlyMap<PlayerId, string>;
  selfId: PlayerId;
}

/**
 * The marks for one end, correct callers first — they are the subject of the
 * chip, and the misses read as the aside they are.
 */
export function attributionChip(input: AttributionChipInput): AttributionMark[] {
  const { end, attribution, names, selfId } = input;
  if (!attribution) return [];

  const calledTop = attribution.predictedTopBy ?? [];
  const calledBottom = attribution.predictedBottomBy ?? [];
  const [right, wrong] = end === 'first' ? [calledTop, calledBottom] : [calledBottom, calledTop];

  const mark = (correct: boolean) => (playerId: PlayerId): AttributionMark => ({
    playerId,
    name: playerId === selfId ? 'You' : (names.get(playerId) ?? 'Someone'),
    correct,
  });

  return [...right.map(mark(true)), ...wrong.map(mark(false))];
}
